import React, { ReactElement, useState } from 'react'
import {
  alertError,
  alertSuccess,
  AsyncButton,
  capitalize,
  Button,
  ButtonSize,
  ButtonStyle,
  Checkbox,
  colors,
  GiftItemStatus,
  GiftItemType,
  IGiftItemResponse,
  isPromotionDisabled,
  ISelectOption,
  Modal,
  OrganisationFormat,
  SelectField,
  StateAction,
  stripHTML,
  formatMoney,
  FieldSize,
} from '@one-tree/library'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import download from 'downloadjs'
import { useOrganisation } from '../../../context/OrganisationProvider'
import {
  bulkPatchGiftItems,
  getEventGuestlist,
} from '../../../helpers/APIItemHelper'
import {
  getRemaining,
  getTotalSold,
  getTotalProfit,
  getPrices,
  getValues,
  getTotalDisplay,
  getQuantities,
  getDate,
} from './giftItemTableHelper'
import { RoutePath } from '../../../types/Routes'
import { ICategoryResponse } from '../../../types/API'
import PublishSwitch from '../../../components/PublishSwitch'
import {
  handleRestoreItem as restore,
  handleArchiveItem as archive,
} from '../../../helpers/GiftItemHelper'
import { isGiftItemValid } from '../../../helpers/GiftItemValidityHelper'
import { editPromotion } from '../../../helpers/APIHelper'
import {
  convertPromoResponseToPromotion,
  convertPromotionToPromoResponse,
  isPromoActive,
  Promotion,
  PromoDot,
  PromoOption,
  Promotions,
} from '../promotion/PromotionHelper'

const Table = styled.table<{ processing?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  margin-top: 10px;

  th,
  td {
    padding: 13px 10px;
    text-align: left;
    border-bottom: 1px solid ${colors.lightGray};
    vertical-align: middle;
  }

  th {
    background-color: ${colors.lightestGray};
    border-bottom: 1px solid ${colors.gray};
    font-weight: bold;
    white-space: nowrap;
  }

  /* Remove inline spacing for checkbox */
  th:first-child,
  td:first-child {
    line-height: 0;
    width: 1%;
  }

  a {
    color: ${colors.blue};
  }

  a:hover {
    color: ${colors.lightBlue};
  }
`
const NoBreak = styled.td`
  white-space: nowrap;
`

const TotalsRow = styled.tr`
  font-weight: bold;
  background-color: ${colors.lightestGray};
  && td {
    border-top: ${colors.darkestGray};
    border-bottom: none;
  }
`
const SelectionBar = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 15px;
  border: 1px solid ${colors.darkGray};
  border-radius: 4px;
  padding: 10px 15px;
  & > :first-child {
    margin-right: 10px;
  }
`
const StyledModal = styled(Modal)`
  width: 250px;
  p {
    font-weight: bold;
    font-size: 1.1rem;
    padding: 5px 5px 0;
  }
`
const ModalButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
`

function ConfirmModal({
  open,
  onClose,
  message,
  confirmLabel,
  confirmStyle,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  message: string
  confirmLabel: string
  confirmStyle: ButtonStyle
  onConfirm: () => Promise<void>
}): ReactElement {
  return (
    <StyledModal modalOpen={open} onModalClose={onClose}>
      <p>{message}</p>
      <ModalButtons>
        <Button buttonStyle={ButtonStyle.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <AsyncButton buttonStyle={confirmStyle} onClick={onConfirm}>
          {confirmLabel}
        </AsyncButton>
      </ModalButtons>
    </StyledModal>
  )
}

type ModalType = 'archive' | 'restore' | 'publish' | 'unpublish' | 'promotion'

type Props = {
  items: IGiftItemResponse[]
  setItems: StateAction<IGiftItemResponse[]>
  archived: boolean
  promotions?: Promotions
  onPromoUpdate: (promo: Promotion) => void
  categories?: ICategoryResponse[]
}

export default function GiftItemTable(props: Props): ReactElement {
  const { items, setItems, archived, promotions, onPromoUpdate, categories } = props
  const { organisation } = useOrganisation()
  const [selected, setSelected] = useState<number[]>([])
  const [openModal, setOpenModal] = useState<ModalType | null>(null)
  const [selectedPromoId, setSelectedPromoId] = useState<number | null>(null)

  const visibleSelected = items.filter(
    ({ id }) => id !== undefined && selected.includes(id),
  )
  const canPublish = visibleSelected.some(
    (item) => isGiftItemValid(item, organisation, undefined, categories)
      && item.status === GiftItemStatus.Unpublished
      && item.type !== GiftItemType.Complimentary,
  )
  const canUnpublish = visibleSelected.some(
    ({ status }) => status === GiftItemStatus.Published,
  )

  const all = items.length > 0 && visibleSelected.length === items.length
  const toggleAll = (): void => {
    setSelected(
      all ? [] : items.flatMap(({ id }) => (id !== undefined ? [id] : [])),
    )
  }
  const toggleOne = (id: number): void => {
    setSelected((p) => (p.includes(id) ? p.filter((selectedId) => selectedId !== id) : [...p, id]))
  }

  const isTicket = organisation?.format === OrganisationFormat.Tickets
  const isOffer = organisation?.format === OrganisationFormat.SpecialOffers
  const isGiftVoucher = organisation?.format === OrganisationFormat.GiftVouchers

  const handlePublish = async (): Promise<void> => {
    if (!organisation) return
    const patchOptions = visibleSelected
      .filter(
        (item) => isGiftItemValid(item, organisation, undefined, categories)
          && item.status === GiftItemStatus.Unpublished
          && item.type !== GiftItemType.Complimentary
          && item.id !== undefined,
      )
      .map((item) => ({ id: item.id, status: GiftItemStatus.Published }))

    const res = await bulkPatchGiftItems({
      format: organisation.format,
      patchOptions,
    })

    if (res) {
      alertSuccess(`${res.length} item${res.length > 1 ? 's' : ''} published`)
      setItems((prev) => prev.map((item) => {
          const updated = res.find((r) => r.id === item.id)
          return updated ? { ...item, status: updated.status } : item
        }))
      setSelected([])
      setOpenModal(null)
    } else {
      alertError('Error publishing items')
    }
  }

  const handleUnpublish = async (): Promise<void> => {
    if (!organisation) return
    const patchOptions = visibleSelected
      .filter(
        (item) => item.status === GiftItemStatus.Published && item.id !== undefined,
      )
      .map((item) => ({ id: item.id, status: GiftItemStatus.Unpublished }))

    const res = await bulkPatchGiftItems({
      format: organisation.format,
      patchOptions,
    })

    if (res) {
      alertSuccess(`${res.length} item${res.length > 1 ? 's' : ''} unpublished`)
      setItems((prev) => prev.map((item) => {
          const updated = res.find((r) => r.id === item.id)
          return updated ? { ...item, status: updated.status } : item
        }))
      setSelected([])
      setOpenModal(null)
    } else {
      alertError('Error unpublishing items')
    }
  }

  const handleArchive = async (): Promise<void> => {
    const ids = visibleSelected.flatMap(({ id }) => (id !== undefined ? [id] : []))
    await Promise.all(ids.map((id) => archive(organisation, setItems, id)))
    setSelected([])
    setOpenModal(null)
  }

  const handleRestore = async (): Promise<void> => {
    const ids = visibleSelected.flatMap(({ id }) => (id !== undefined ? [id] : []))
    await Promise.all(ids.map((id) => restore(organisation, setItems, id)))
    setSelected([])
    setOpenModal(null)
  }

  const promoOptions: ISelectOption<number>[] = (promotions ?? [])
    .map((promo) => ({
      label: (
        <PromoOption>
          <PromoDot active={isPromoActive(promo)} />
          {promo.name}
        </PromoOption>
      ),
      value: promo.id,
    }))

  const closePromoModal = (): void => {
    setOpenModal(null)
    setSelectedPromoId(null)
  }

  const handleAddPromotion = async (): Promise<void> => {
    if (!selectedPromoId || !promotions) return

    const promo = promotions.find((p) => p.id === selectedPromoId)
    if (!promo) return

    const convertedPromo = convertPromotionToPromoResponse(promo)
    const compatibleItems = visibleSelected
      .filter((item) => !isPromotionDisabled({ ...item, promotion: convertedPromo }))
      .flatMap(({ id }) => (id !== undefined ? [id] : []))

    if (!compatibleItems.length) {
      alertError('No selected items are compatible with this promotion')
      return
    }

    const skipped = visibleSelected.length - compatibleItems.length
    const existing = promo.giftItems ?? []
    const newGiftItems = existing.concat(
      compatibleItems.filter((id) => !existing.includes(id)),
    )

    const res = await editPromotion({
      id: promo.id,
      promotion: { giftItems: newGiftItems },
    })

    if (res) {
      onPromoUpdate(convertPromoResponseToPromotion(res))
      const added = compatibleItems.length
      alertSuccess(
        `Promotion added to ${added} item${added > 1 ? 's' : ''}${skipped ? `. Not added to ${skipped} incompatible item${skipped > 1 ? 's' : ''}` : ''}.`,
      )
      closePromoModal()
    } else {
      alertError('Error adding promotion')
    }
  }

  const handleRemovePromotion = async (): Promise<void> => {
    if (!selectedPromoId || !promotions) return

    const promo = promotions.find((p) => p.id === selectedPromoId)
    if (!promo) return

    const visibleIds = visibleSelected.flatMap(({ id }) => (id !== undefined ? [id] : []))
    const removedCount = visibleIds.filter((id) => promo.giftItems?.includes(id)).length
    if (!removedCount) {
      alertError('None of the selected items have this promotion')
      return
    }

    const newGiftItems = (promo.giftItems ?? []).filter(
      (id) => !visibleIds.includes(id),
    )

    const res = await editPromotion({
      id: promo.id,
      promotion: { giftItems: newGiftItems },
    })

    if (res) {
      onPromoUpdate(convertPromoResponseToPromotion(res))
      alertSuccess(
        `Promotion removed from ${removedCount} item${removedCount > 1 ? 's' : ''}`,
      )
      closePromoModal()
    } else {
      alertError('Error removing promotion')
    }
  }

  const modalConfig = {
    archive: { style: ButtonStyle.Danger, action: handleArchive },
    restore: { style: ButtonStyle.Action, action: handleRestore },
    publish: { style: ButtonStyle.Action, action: handlePublish },
    unpublish: { style: ButtonStyle.Secondary, action: handleUnpublish },
  }

  const onSwitch = (res: IGiftItemResponse): void => {
    setItems((p) => {
      const index = p.findIndex((item) => item.id === res.id)
      if (index !== -1) {
        const updatedItems = [...p]
        updatedItems[index] = {
          ...updatedItems[index],
          status: res.status,
        }
        return updatedItems
      }
      return p
    })
  }

  const downloadGuestlist = async (
    id?: number,
    name?: string,
  ): Promise<void> => {
    if (!id || !name) {
      alertError('Error downloading guestlist')
      return
    }

    const res = await getEventGuestlist({ id })
    if (res) {
      const notChars = /[_\W]+/g
      download(res, `guestlist-${name.replace(notChars, '')}.csv`)
    }
  }

  const offerHeader = isOffer ? (
    <>
      <th>Start date</th>
      <th>Expiry date</th>
      <th>Sold</th>
      <th>Price</th>
      <th>Sale Price</th>
      <th>Total</th>
    </>
  ) : (
    <th>Description</th>
  )

  return (
    <>
      <SelectionBar>
        <span>
          <b>{visibleSelected.length}</b>
          {' of '}
          <b>{items.length}</b>
          {' selected'}
        </span>
        {archived ? (
          <Button
            buttonSize={ButtonSize.Mini}
            buttonStyle={ButtonStyle.Action}
            onClick={(): void => setOpenModal('restore')}
            disabled={!visibleSelected.length}
          >
            Restore
          </Button>
        ) : (
          <>
            <Button
              buttonSize={ButtonSize.Mini}
              buttonStyle={ButtonStyle.Action}
              onClick={(): void => setOpenModal('publish')}
              disabled={!canPublish}
            >
              Publish
            </Button>
            <Button
              buttonSize={ButtonSize.Mini}
              buttonStyle={ButtonStyle.Secondary}
              onClick={(): void => setOpenModal('unpublish')}
              disabled={!canUnpublish}
            >
              Unpublish
            </Button>
            <Button
              buttonSize={ButtonSize.Mini}
              buttonStyle={ButtonStyle.Danger}
              onClick={(): void => setOpenModal('archive')}
              disabled={!visibleSelected.length}
            >
              Archive
            </Button>
            {isGiftVoucher
              && organisation?.hasPromotions
              && !!promotions?.length && (
                <Button
                  buttonSize={ButtonSize.Mini}
                  onClick={(): void => setOpenModal('promotion')}
                  disabled={!visibleSelected.length}
                >
                  Manage promotion
                </Button>
              )}
          </>
        )}
      </SelectionBar>
      <Table>
        <thead>
          <tr>
            <th>
              <Checkbox
                fieldSize={FieldSize.Large}
                value={all}
                onChange={toggleAll}
              />
            </th>
            <th>Name</th>
            {isTicket ? (
              <>
                <th>Remaining</th>
                <th>Date (and time)</th>
                <th>Type</th>
                <th>Sold</th>
                <th>Price</th>
                <th>Total</th>
              </>
            ) : (
              offerHeader
            )}
            {!archived && <th>Status</th>}
            {isGiftVoucher && organisation?.hasPromotions && (
              <th>Promotions</th>
            )}
            {isTicket && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {items
            && items.map((item: IGiftItemResponse) => {
              const renderOffer = isOffer ? (
                <>
                  <td>{getDate(item.startDate)}</td>
                  <td
                    style={{
                      color:
                        item.expiryDate && item.expiryDate * 1000 < Date.now()
                          ? colors.red
                          : 'inherit',
                    }}
                  >
                    {getDate(item.expiryDate)}
                  </td>
                  <td>{getQuantities(item)}</td>
                  <td>
                    {item.originalPrice ? (
                      <span style={{ textDecoration: 'line-through' }}>
                        {formatMoney(
                          item?.originalPrice || 0,
                          organisation.currencyCode,
                        )}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{getValues(item, organisation.currencyCode)}</td>
                  <td>{getTotalDisplay(item, organisation.currencyCode)}</td>
                </>
              ) : (
                <td>{stripHTML(item.description || '')}</td>
              )

              const { id } = item
              if (id === undefined) return null

              const itemPromos = (promotions ?? []).filter((promo) => promo.giftItems.includes(id))

              return (
                <tr key={id} draggable={false}>
                  <td>
                    <Checkbox
                      fieldSize={FieldSize.Large}
                      value={selected.includes(id)}
                      onChange={(): void => toggleOne(id)}
                    />
                  </td>
                  <td>
                    <Link to={`${RoutePath.ItemGrid}/${item.id}`}>
                      {item.name}
                    </Link>
                  </td>
                  {isTicket ? (
                    <>
                      <td>{getRemaining(item)}</td>
                      <td>{item.startInfo}</td>
                      <NoBreak>{getPrices(item)}</NoBreak>
                      <td>{getQuantities(item)}</td>
                      <td>{getValues(item, organisation.currencyCode)}</td>
                      <td>
                        {getTotalDisplay(item, organisation.currencyCode)}
                      </td>
                    </>
                  ) : (
                    renderOffer
                  )}
                  {!archived && (
                    <td>
                      <PublishSwitch
                        item={item}
                        onSwitch={onSwitch}
                        flipLabel={true}
                        categories={categories}
                      />
                    </td>
                  )}
                  {isGiftVoucher && organisation?.hasPromotions && (
                    <td>
                      {itemPromos.length
                        ? itemPromos.map((promo) => (
                          <div key={promo.id} style={{ marginBottom: '3px' }}>
                            {'• '}
                            <Link
                              to={`${RoutePath.ManagePromotions}/${promo.id}`}
                            >
                              {promo.name}
                            </Link>
                          </div>
                          ))
                        : '-'}
                    </td>
                  )}
                  {isTicket && (
                    <td>
                      {item?.quantitySold ? (
                        <Button
                          buttonSize={ButtonSize.Mini}
                          buttonStyle={ButtonStyle.Secondary}
                          onClick={(): Promise<void> => downloadGuestlist(item.id, item.name)}
                        >
                          Guestlist
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          {isTicket && items.length > 0 && (
            <TotalsRow>
              <td />
              <td />
              <td />
              <td />
              <td>Total:</td>
              <td>{getTotalSold(items)}</td>
              <td />
              <td>{getTotalProfit(items, organisation.currencyCode) || 0}</td>
              <td />
              <td />
              <td />
            </TotalsRow>
          )}
        </tbody>
      </Table>
      {openModal && openModal !== 'promotion' && (
        <ConfirmModal
          open={true}
          onClose={(): void => setOpenModal(null)}
          message={`Are you sure you want to ${openModal} this selection?`}
          confirmLabel={capitalize(openModal)}
          confirmStyle={modalConfig[openModal].style}
          onConfirm={modalConfig[openModal].action}
        />
      )}
      {openModal === 'promotion' && (
        <StyledModal modalOpen={true} onModalClose={closePromoModal}>
          <p>Manage selection promotion</p>
          <SelectField
            options={promoOptions}
            value={
              promoOptions.find((o) => o.value === selectedPromoId) ?? null
            }
            onChange={(option): void => setSelectedPromoId(option?.value ?? null)}
            placeholder="Select a promotion..."
          />
          <ModalButtons>
            <Button
              buttonStyle={ButtonStyle.Secondary}
              onClick={closePromoModal}
            >
              Cancel
            </Button>
            <AsyncButton
              buttonStyle={ButtonStyle.Danger}
              onClick={handleRemovePromotion}
              disabled={!selectedPromoId}
            >
              Remove
            </AsyncButton>
            <AsyncButton
              buttonStyle={ButtonStyle.Action}
              onClick={handleAddPromotion}
              disabled={!selectedPromoId}
            >
              Add
            </AsyncButton>
          </ModalButtons>
        </StyledModal>
      )}
    </>
  )
}
